import { del, get, patch, post, type MtpRequestConfig } from './client'
import type {
  CreatedUser,
  Me,
  Member,
  Page,
  PageQuery,
  Quota,
  Role,
  Tenant,
  TokenPair,
  User,
} from '@/types/api'

/** 登录/刷新必须 `skipAuthRefresh`，否则 401 时拦截器会拿失效的 refresh 再刷一次。 */
const NO_REFRESH: MtpRequestConfig = { skipAuthRefresh: true }

export function login(username: string, password: string): Promise<TokenPair> {
  return post<TokenPair>('/auth/login', { username, password }, NO_REFRESH)
}

export function logout(): Promise<void> {
  return post<void>('/auth/logout')
}

export function fetchMe(): Promise<Me> {
  return get<Me>('/me')
}

/** 自助改密。204 无响应体。旧 token 会因 `token_version` 递增而全部失效。 */
export function changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
  return post<void>('/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

// ── 用户与成员（admin）──────────────────────────────────────

/**
 * `q` 按用户名 / 昵称 / 邮箱模糊搜，**在库里搜**（不是前端过滤当页）。
 *
 * @param query.tenant_id 指名要看哪个租户的成员（契约 §35.6 的作用域参数）。
 *   **只有 superadmin 带得动**：非 superadmin 传它——哪怕传的就是自己那一个——
 *   一律 403 `FORBIDDEN` 并留 `authz.denied`。不传 = 操作者当前租户，
 *   与加这个参数之前逐字相同。租户不存在或已软删 404 `NOT_FOUND`。
 *   没有全平台视图：本端点不收 `unscoped`。
 */
export function listUsers(
  query: PageQuery & { q?: string; tenant_id?: string } = {},
): Promise<Page<Member>> {
  return get<Page<Member>>('/users', query)
}

/**
 * **一个租户都不属于**的用户（超管专属）。
 *
 * 用户与角色页按租户列成员，所以零成员关系的人一列都不在 —— 他们从整个后台消失，
 * 而 `users` 那一行还活着。这条是把他们捞回来的唯一入口。
 *
 * 孤儿有两条来源：移出最后一个租户（这条已被 409 `LAST_MEMBERSHIP` 拦住），
 * 以及**清租**（那个租户的全部成员关系被删，只属于它的人就成了孤儿）——
 * 后者不受那道 409 约束，所以这个入口长期需要。
 *
 * 出参是 `User` 不是 `Member`：他们没有成员关系，也就没有「在哪个租户里的角色」。
 */
export function listOrphanUsers(
  query: PageQuery & { q?: string } = {},
): Promise<Page<User>> {
  return get<Page<User>>('/users/orphans', query)
}

/** 建成员。**不收 password**——后端生成一次性临时口令并在响应里返回，
 *  此后任何接口都拿不到。前端必须当场展示给管理员。 */
export function createUser(body: {
  username: string
  display_name: string
  role: Role
  /** 目标租户；不传 = 操作者当前租户。superadmin 专属端点，跨租户开号是它的业务。 */
  tenant_id?: string
  /** 平台级身份，跨越所有租户与角色。 */
  is_superadmin?: boolean
}): Promise<CreatedUser> {
  return post<CreatedUser>('/users', body)
}

/** **PATCH** 而不是 POST —— 后端是 `patch("/{user_id}/role")`。 */
export function updateUserRole(userId: string, role: Role): Promise<Member> {
  return patch<Member>(`/users/${userId}/role`, { role })
}

/** 重置口令。返回新的一次性临时口令；旧的 refresh token 立刻失效。 */
export function resetUserPassword(userId: string): Promise<{ temporary_password: string }> {
  return post<{ temporary_password: string }>(`/users/${userId}/reset-password`)
}

/**
 * 解除登录限制（连错口令触发的锁定与限速）。幂等：没锁也 200，
 * `was_locked` 告诉调用方该给哪句提示。
 */
export function unlockUserLogin(
  userId: string,
): Promise<{ was_locked: boolean; cleared_failures: number }> {
  return post<{ was_locked: boolean; cleared_failures: number }>(`/users/${userId}/unlock-login`)
}

/**
 * 停用账号（superadmin）。**所有租户**一起挡住，并让 TA 当前的登录立刻失效。
 *
 * @param reason 停用理由，只进审计日志。
 */
export function disableUser(userId: string, reason?: string): Promise<Member> {
  return post<Member>(`/users/${userId}/disable`, reason ? { reason } : {})
}

/** 恢复被停用的账号（superadmin）。不发新口令，也不改「首次改密」标志。 */
export function enableUser(userId: string): Promise<Member> {
  return post<Member>(`/users/${userId}/enable`)
}

/**
 * 授予 / 撤销平台管理员（superadmin）。返回的是 `User` 而不是 `Member` ——
 * 目标可能不是本租户成员。
 *
 * 幂等：目标状态已成立时照样 200 并原样返回。改动当场生效，并让 TA 已签发的
 * token 全部失效。
 *
 * 409 `LAST_SUPERADMIN`（撤销最后一个）与 409 `CANNOT_CHANGE_OWN_SUPERADMIN`
 * （撤销自己）由后端拦，`message` 已面向用户。
 */
export function setUserSuperadmin(userId: string, isSuperadmin: boolean): Promise<User> {
  return patch<User>(`/users/${userId}/superadmin`, { is_superadmin: isSuperadmin })
}

// ── 租户（admin）─────────────────────────────────────────────

/** `q` 按名字或 slug 模糊搜。非 superadmin 只可能搜到自己那一个。 */
export function listTenants(query: PageQuery & { q?: string } = {}): Promise<Page<Tenant>> {
  return get<Page<Tenant>>('/tenants', query)
}

/**
 * 把一个**已存在**的用户加进某个租户。路径与请求体里都没有 user_id ——
 * 按用户名指定（按 id 邀请的端点等于平台级 user id 探测器，契约 §10.8.8 第 10 条）。
 *
 * @param tenantId 目标租户；不传 = 操作者当前租户。传别的租户需要 superadmin，
 *   否则 403 `detail.required = ["is_superadmin"]`。
 */
export function inviteMember(username: string, role: Role, tenantId?: string): Promise<Member> {
  return post<Member>('/users/memberships', {
    username,
    role,
    ...(tenantId ? { tenant_id: tenantId } : {}),
  })
}

/** 移出成员。只断关系，用户本身还在（要真删走 superadmin 的删除用户）。 */
export function removeMember(userId: string): Promise<void> {
  return del<void>(`/users/${userId}/membership`)
}

/** 软删用户（superadmin）。永不物理删 —— 历史任务的 `created_by` 要能指过去。 */
export function deleteUser(userId: string): Promise<void> {
  return del<void>(`/users/${userId}`)
}

export function createTenant(body: {
  slug: string
  name: string
  description?: string
}): Promise<Tenant> {
  return post<Tenant>('/tenants', body)
}

export function updateTenant(
  tenantId: string,
  body: { name?: string; description?: string; is_active?: boolean },
): Promise<Tenant> {
  return patch<Tenant>(`/tenants/${tenantId}`, body)
}

/**
 * 我能进哪几个租户（未停用的）。左下角那个切换器读它。
 *
 * **不收任何参数**：能进哪几个只能由服务端按成员关系算出来。
 */
export function fetchMyTenants(): Promise<Tenant[]> {
  return get<Tenant[]>('/me/tenants')
}

/**
 * 切到另一个租户，拿一对**新票**。
 *
 * ★ 不是改前端状态：`tenant_id` 只从 token 取是整套隔离的地基，让前端指定要看哪个
 * 租户正是它防的东西。换票之后从鉴权到仓储每一层自动指向新租户。
 *
 * 服务端顺带把 `users.default_tenant_id` 写成这一个 —— 「下次登录默认落在上次待的
 * 那个租户」就是这一行。
 */
export function switchTenant(tenantId: string): Promise<TokenPair> {
  return post<TokenPair>('/me/switch-tenant', { tenant_id: tenantId })
}

/** 一次清理请求的登记回执。**不是「已删完」** —— 真正的删除是后台状态机。 */
export interface TenantPurgeAck {
  purge_id: string
  state: string
  tenant_id: string
  detail: string
}

/**
 * 登记一次租户清理（superadmin，**不可逆**）。
 *
 * 两道护栏在后端，前端一条都不能替它省：
 * - 必须**先停用**那个租户，否则被拒；
 * - `confirm_name` 要**逐字**等于 `tenants.name`。后端**刻意不回显正确答案** ——
 *   回显等于把打字确认退化成一次粘贴，所以这一侧也不提供「复制名称」。
 *
 * 有未结束的任务时默认被拒；要连它们一起停，得显式带 `stop_running` 再来一次。
 *
 * 返回的是**登记回执**：接口 202，真正的删除是 `tenant_purges` 状态机，
 * 几十分钟起步。别把它当成「已经删干净了」。
 */
export function purgeTenant(
  tenantId: string,
  body: { confirm_name: string; stop_running?: boolean },
): Promise<TenantPurgeAck> {
  // `DELETE /tenants/{id}` 带 body —— 后端没有 `/purge` 这个子路径。
  return del<TenantPurgeAck>(`/tenants/${tenantId}`, { data: body })
}

// ── 配额 ─────────────────────────────────────────────────────

export function fetchQuota(tenantId?: string): Promise<Quota> {
  return get<Quota>(tenantId ? `/quotas/${tenantId}` : '/quotas/me')
}

/** 改限额（superadmin）。**空 body 会 422** —— 后端刻意不接受「什么都不改的保存」。 */
export function updateQuota(tenantId: string, changes: Record<string, unknown>): Promise<Quota> {
  return patch<Quota>(`/quotas/${tenantId}`, changes)
}

/** 改自己的资料。**只有显示名与邮箱** —— 用户名是审计里的 `actor_label`，改不得。 */
export function updateMe(body: { display_name?: string; email?: string | null }): Promise<Me> {
  return patch<Me>('/me', body)
}
